<script setup>
import { computed, onMounted, ref, watchEffect } from "vue";
import { useAuthStore } from "@/stores/useAuthStore";
import { usePage } from "@inertiajs/inertia-vue3";

const props = defineProps({
  tag: { type: [String, Array], default: null },
  models: { type: Array, default: () => [] },
  isVisible: { type: Boolean, default: false },
});

const authStore = useAuthStore();
const pageProps = computed(() => usePage().props.value);
const user = computed(() => pageProps.value.auth?.user || {});

onMounted(() => {
  authStore.fetchAuthMetadata();
});

// Calcul de la liste de permissions requises
const requiredPermissions = computed(() => {
  if (props.tag) {
    // Si c'est un tableau de tags
    if (Array.isArray(props.tag)) {
      return props.tag.reduce((acc, t) => {
        const perms = authStore.permissionsMap[t] || [];
        return [...acc, ...perms];
      }, []);
    }
    // Si c'est un tag unique
    return authStore.permissionsMap[props.tag] || [];
  }
  return props.models;
});

// Sets pour la performance (O(1))
const authRolesSet = computed(() => new Set(user.value.roles?.map(r => r.name) || []));
const authPermissionsSet = computed(() => new Set(user.value.permissions?.map(p => p.name) || []));

const rolePermissionsSet = computed(() => {
  const perms = new Set();
  user.value.roles?.forEach(role => {
    role.permissions?.forEach(p => perms.add(p.name));
  });
  return perms;
});

// Résultat global du droit
const can = computed(() => {
  if (!user.value?.id) return false;

  return requiredPermissions.value.some(item =>
    authPermissionsSet.value.has(item) ||
    authRolesSet.value.has(item) ||
    rolePermissionsSet.value.has(item)
  );
});

// Helpers exposés au slot pour des vérifications précises dans le HTML
const hasRole = (role) => authRolesSet.value.has(role);
const hasPermission = (p) => authPermissionsSet.value.has(p) || rolePermissionsSet.value.has(p);

const canNote = ref(null);
watchEffect(() => {
  canNote.value = can.value ? null : "Accès restreint";
});
</script>

<template>
  <slot
    :can="can"
    :hasRole="hasRole"
    :hasPermission="hasPermission"
    :canNote="canNote"
    :isVisible="isVisible"
  ></slot>
</template>
